import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  IconButton,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Avatar,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  TimelineEventItem,
  TimelineShareTarget,
  GeneralAccessLevel,
  ShareAccessRole,
  SeasonRoster,
} from '../../../types';

export interface TimelineShareModalProps {
  open: boolean;
  onClose: () => void;
  event: TimelineEventItem | null;
  rosters: Record<string, SeasonRoster>;
  onSaveShares: (shareData: {
    generalAccess: GeneralAccessLevel;
    publicRole: ShareAccessRole;
    shares: TimelineShareTarget[];
  }) => Promise<void>;
  onCopyLink?: (link: string) => void;
}

interface ShareCandidate {
  type: 'user' | 'player';
  id: string;
  name: string;
  subtitle: string;
  parentEmails?: string[];
  parentNames?: string[];
}

export const TimelineShareModal: React.FC<TimelineShareModalProps> = ({
  open,
  onClose,
  event,
  rosters,
  onSaveShares,
  onCopyLink,
}) => {
  const [generalAccess, setGeneralAccess] = useState<GeneralAccessLevel>(() => event?.generalAccess || 'private');
  const [publicRole, setPublicRole] = useState<ShareAccessRole>(() => event?.publicRole || 'viewer');
  const [shares, setShares] = useState<TimelineShareTarget[]>(() => event?.shares || []);
  const [selectedCandidate, setSelectedCandidate] = useState<ShareCandidate | null>(null);
  const [newShareRole, setNewShareRole] = useState<ShareAccessRole>('viewer');
  const [saving, setSaving] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Sync state when event prop changes
  React.useEffect(() => {
    if (event) {
      setGeneralAccess(event.generalAccess || 'private');
      setPublicRole(event.publicRole || 'viewer');
      setShares(event.shares || []);
    }
  }, [event]);

  // Aggregate candidate users and players across all season rosters
  const candidates: ShareCandidate[] = useMemo(() => {
    const list: ShareCandidate[] = [];
    const seenEmails = new Set<string>();
    const seenPlayers = new Set<string>();

    Object.values(rosters).forEach((roster) => {
      // 1. Players
      (roster.players || []).forEach((p) => {
        const pKey = `${p.firstName}_${p.lastName}_${p.jersey || ''}`.toLowerCase();
        if (!seenPlayers.has(pKey)) {
          seenPlayers.add(pKey);
          const parentNames = (p.parents || []).map((par) => par.name).filter(Boolean);
          const parentEmails = (p.parents || []).map((par) => par.email).filter(Boolean) as string[];
          list.push({
            type: 'player',
            id: `player_${p.firstName}_${p.lastName}_${p.jersey || ''}`.toLowerCase(),
            name: `${p.firstName} ${p.lastName} ${p.jersey ? `#${p.jersey}` : ''}`.trim(),
            subtitle: parentNames.length > 0 ? `Parents: ${parentNames.join(', ')}` : 'Rostered Player',
            parentNames,
            parentEmails,
          });
        }

        // 2. Parents as individual candidates
        (p.parents || []).forEach((par) => {
          const emailNorm = (par.email || '').trim().toLowerCase();
          if (emailNorm && !seenEmails.has(emailNorm)) {
            seenEmails.add(emailNorm);
            list.push({
              type: 'user',
              id: emailNorm,
              name: par.name || emailNorm,
              subtitle: `Parent of ${p.firstName} ${p.lastName}`,
            });
          }
        });
      });

      // 3. Staff
      (roster.staff || []).forEach((s) => {
        const emailNorm = (s.email || '').trim().toLowerCase();
        if (emailNorm && !seenEmails.has(emailNorm)) {
          seenEmails.add(emailNorm);
          list.push({
            type: 'user',
            id: emailNorm,
            name: `${s.firstName} ${s.lastName}`.trim(),
            subtitle: s.role || 'Staff / Coach',
          });
        }
      });
    });

    return list;
  }, [rosters]);

  if (!event) return null;

  const handleAddShare = () => {
    if (!selectedCandidate) return;

    // Check if already in shares list
    const existingIdx = shares.findIndex((s) => s.id.toLowerCase() === selectedCandidate.id.toLowerCase());
    if (existingIdx >= 0) {
      // Update role
      const updated = [...shares];
      updated[existingIdx].access = newShareRole;
      setShares(updated);
    } else {
      setShares([
        ...shares,
        {
          type: selectedCandidate.type,
          id: selectedCandidate.id,
          label: selectedCandidate.name,
          access: newShareRole,
        },
      ]);
    }
    setSelectedCandidate(null);
  };

  const handleRemoveShare = (id: string) => {
    setShares(shares.filter((s) => s.id.toLowerCase() !== id.toLowerCase()));
  };

  const handleUpdateRole = (id: string, access: ShareAccessRole) => {
    setShares(
      shares.map((s) => {
        if (s.id.toLowerCase() === id.toLowerCase()) {
          return { ...s, access };
        }
        return s;
      })
    );
  };

  const handleCopyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('event', event.id);
    navigator.clipboard.writeText(url.toString());
    setCopyFeedback(true);
    if (onCopyLink) {
      onCopyLink(url.toString());
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveShares({
        generalAccess,
        publicRole,
        shares,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1c1c1f',
            color: '#fff',
            borderRadius: 2.5,
            border: '1px solid rgba(255, 255, 255, 0.12)',
            p: 0.5,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ShareIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#fff' }}>
              Share {event.eventType === 'note' ? 'Note' : `"${(event as any).title || 'Clip'}"`}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {/* Add People and Players Input */}
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600, mb: 1, display: 'block' }}>
            Add people or rostered players:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Autocomplete
              options={candidates}
              value={selectedCandidate}
              onChange={(_, val) => setSelectedCandidate(val)}
              getOptionLabel={(opt) => opt.name}
              filterOptions={(options, state) => {
                const query = state.inputValue.toLowerCase();
                return options.filter(
                  (opt) =>
                    opt.name.toLowerCase().includes(query) ||
                    opt.subtitle.toLowerCase().includes(query) ||
                    opt.id.toLowerCase().includes(query)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: option.type === 'player' ? '#0284c7' : '#475569', fontSize: 13 }}>
                      {option.type === 'player' ? <SportsHockeyIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                        {option.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search parents, coaches, or players..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                      '&:hover fieldset': { borderColor: '#38bdf8' },
                    },
                  }}
                />
              )}
            />

            <FormControl size="small" sx={{ width: 110 }}>
              <Select
                value={newShareRole}
                onChange={(e) => setNewShareRole(e.target.value as ShareAccessRole)}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
                  fontSize: '0.82rem',
                }}
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleAddShare}
              disabled={!selectedCandidate}
              sx={{
                bgcolor: '#38bdf8',
                color: '#000',
                fontWeight: 700,
                textTransform: 'none',
                px: 2,
                '&:hover': { bgcolor: '#7dd3fc' },
              }}
            >
              Add
            </Button>
          </Stack>

          {/* General Access Section (Google Docs Style) */}
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600, mb: 1, display: 'block' }}>
            General access:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              mb: 3,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: generalAccess === 'public' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.08)' }}>
                {generalAccess === 'public' ? <PublicIcon sx={{ fontSize: 18, color: '#38bdf8' }} /> : <LockIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)' }} />}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                  {generalAccess === 'public' ? 'Anyone with team site access' : 'Restricted (Private)'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                  {generalAccess === 'public'
                    ? 'Anyone authorized on the Chicago Jets site can view'
                    : 'Only people and players explicitly shared below can access'}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ width: 120 }}>
                <Select
                  data-testid="general-access-select"
                  value={generalAccess === 'public' ? 'public' : 'restricted'}
                  onChange={(e) => setGeneralAccess(e.target.value as GeneralAccessLevel)}
                  sx={{
                    color: '#fff',
                    fontSize: '0.82rem',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
                  }}
                >
                  <MenuItem value="restricted">Restricted</MenuItem>
                  <MenuItem value="public">Anyone (Public)</MenuItem>
                </Select>
              </FormControl>

              {generalAccess === 'public' && (
                <FormControl size="small" sx={{ width: 100 }}>
                  <Select
                    value={publicRole}
                    onChange={(e) => setPublicRole(e.target.value as ShareAccessRole)}
                    sx={{
                      color: '#fff',
                      fontSize: '0.82rem',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
                    }}
                  >
                    <MenuItem value="viewer">Viewer</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Box>

          {/* People with Access List */}
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600, mb: 1, display: 'block' }}>
            People and players with access:
          </Typography>

          <Stack spacing={1}>
            {/* Owner Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, px: 1 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 30, height: 30, bgcolor: '#f59e0b', color: '#000', fontSize: 13, fontWeight: 700 }}>
                  {(event.createdByName || event.createdBy || 'U')[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                    {event.createdByName || event.createdBy} <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>(You)</Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                    {event.createdBy}
                  </Typography>
                </Box>
              </Stack>
              <Chip label="Owner" size="small" sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
            </Box>

            {/* Shares List */}
            {shares.map((s) => {
              const matchedCandidate = candidates.find((c) => c.id.toLowerCase() === s.id.toLowerCase());
              return (
                <Box
                  key={s.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 30, height: 30, bgcolor: s.type === 'player' ? '#0284c7' : '#475569', fontSize: 13 }}>
                      {s.type === 'player' ? <SportsHockeyIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                        {s.label || s.id}
                      </Typography>
                      {s.type === 'player' && matchedCandidate?.parentNames && matchedCandidate.parentNames.length > 0 && (
                        <Typography variant="caption" sx={{ color: '#38bdf8', display: 'block' }}>
                          Inherited by parents: {matchedCandidate.parentNames.join(', ')}
                        </Typography>
                      )}
                      {s.type === 'user' && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                          {s.id}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ width: 95 }}>
                      <Select
                        value={s.access}
                        onChange={(e) => handleUpdateRole(s.id, e.target.value as ShareAccessRole)}
                        sx={{
                          color: '#fff',
                          fontSize: '0.78rem',
                          height: 30,
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
                        }}
                      >
                        <MenuItem value="viewer">Viewer</MenuItem>
                        <MenuItem value="editor">Editor</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton size="small" onClick={() => handleRemoveShare(s.id)} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444' } }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)', justifyContent: 'space-between' }}>
          <Button
            startIcon={<LinkIcon />}
            onClick={handleCopyLink}
            sx={{
              color: '#38bdf8',
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            Copy link
          </Button>

          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={saving} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: '#38bdf8',
                color: '#000',
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#7dd3fc' },
              }}
            >
              {saving ? 'Saving...' : 'Done'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyFeedback}
        autoHideDuration={2500}
        onClose={() => setCopyFeedback(false)}
        message="Link copied to clipboard!"
      />
    </>
  );
};
